import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SEOHead } from "@/components/SEOHead";
import { 
  ArrowRight, Square, Clock, Minus, Plus, CheckCircle,
  Sparkles, Shield, Phone, Star, Home, LayoutGrid,
  MapPin, Calendar, BadgePercent, Banknote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const [activeTab, setActiveTab] = useState("menage");
  const [mode, setMode] = useState("m2");
  const [valeur, setValeur] = useState("");
  const [devisMenage, setDevisMenage] = useState(null);
  
  const [vitres, setVitres] = useState({
    fenetre_simple: 0, fenetre_double: 0, porte_fenetre: 0, baie_vitree: 0,
  });
  const [encadrements, setEncadrements] = useState({
    encadrement_simple: 0, encadrement_double: 0, encadrement_porte_fenetre: 0, encadrement_baie_vitree: 0,
  });
  const [devisVitres, setDevisVitres] = useState(null);

  const vitresItems = [
    { key: "fenetre_simple", label: "Fen\xeatre simple", prix: 6 },
    { key: "fenetre_double", label: "Fen\xeatre double", prix: 9 },
    { key: "porte_fenetre", label: "Porte-fen\xeatre", prix: 12 },
    { key: "baie_vitree", label: "Baie vitr\xe9e", prix: 15 },
  ];

  const encadrementsItems = [
    { key: "encadrement_simple", label: "Encadrement simple", prix: 3 },
    { key: "encadrement_double", label: "Encadrement double", prix: 4 },
    { key: "encadrement_porte_fenetre", label: "Encadrement porte-fen\xeatre", prix: 5 },
    { key: "encadrement_baie_vitree", label: "Encadrement baie vitr\xe9e", prix: 6 },
  ];

  useEffect(() => {
    if (activeTab !== "menage") return;
    const calculate = async () => {
      const num = parseFloat(valeur);
      if (!num || num <= 0) { setDevisMenage(null); return; }
      try {
        const res = await axios.post(`${API}/devis/particulier`, { type_calcul: mode, valeur: num });
        setDevisMenage(res.data);
      } catch (e) { console.error(e); }
    };
    const t = setTimeout(calculate, 300);
    return () => clearTimeout(t);
  }, [valeur, mode, activeTab]);

  useEffect(() => {
    if (activeTab !== "vitres") return;
    const calculate = async () => {
      const hasVitres = Object.values(vitres).some(v => v > 0);
      const hasEncadrements = Object.values(encadrements).some(v => v > 0);
      if (!hasVitres && !hasEncadrements) { setDevisVitres(null); return; }
      try {
        const res = await axios.post(`${API}/devis/vitres`, { ...vitres, ...encadrements });
        setDevisVitres(res.data);
      } catch (e) { console.error(e); }
    };
    const t = setTimeout(calculate, 300);
    return () => clearTimeout(t);
  }, [vitres, encadrements, activeTab]);

  const updateVitre = (key, delta) => {
    setVitres(prev => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));
  };

  const updateEncadrement = (key, delta) => {
    setEncadrements(prev => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));
  };

  const currentDevis = activeTab === "menage" ? devisMenage : devisVitres;

  const CounterRow = ({ item, value, onUpdate }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-800">{item.label}</p>
        <p className="text-xs text-gray-500">{item.prix}{"\u20AC"} / unit\xe9</p>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={() => onUpdate(item.key, -1)}
          className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
          <Minus className="w-4 h-4 text-gray-600" />
        </button>
        <span className="w-8 text-center text-lg font-bold text-[#003264]">{value}</span>
        <button onClick={() => onUpdate(item.key, 1)}
          className="w-9 h-9 rounded-full bg-[#003264] hover:bg-[#002244] flex items-center justify-center text-white transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="pt-16" data-testid="home-page">
      <SEOHead 
        title={"Soci\xe9t\xe9 de Nettoyage dans l'H\xe9rault (34)"}
        description={"IKS Propret\xe9 & Services : votre soci\xe9t\xe9 de nettoyage professionnelle dans l'H\xe9rault. M\xe9nage \xe0 domicile d\xe8s 18\u20AC/h avec l'avance imm\xe9diate du cr\xe9dit d'imp\xf4t."}
        path="/"
        keywords={"soci\xe9t\xe9 de nettoyage H\xe9rault, entreprise de nettoyage Montpellier, m\xe9nage \xe0 domicile 34"}
      />

      {/* ====== HERO ====== */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#003264]" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/[0.03] rounded-full -translate-y-1/3 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/[0.03] rounded-full translate-y-1/3 -translate-x-1/4"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full mb-8">
                <BadgePercent className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-semibold text-white/90">{"Avance imm\xe9diate cr\xe9dit d'imp\xf4t -50%"}</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
                {"Votre m\xe9nage "}
                <br className="hidden sm:block" />
                {"\xe0 domicile "}
                <span className="relative inline-block">
                  <span className="text-emerald-400">{"d\xe8s 18\u20AC/h"}</span>
                  <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path d="M2 6C40 2 120 2 198 6" stroke="#34d399" strokeWidth="3" strokeLinecap="round" opacity="0.5"/>
                  </svg>
                </span>
              </h1>

              <p className="text-lg text-white/70 mb-10 max-w-md leading-relaxed">
                {"Nettoyage professionnel dans tout l'H\xe9rault (34). Vous ne payez que la moiti\xe9, l'\xc9tat verse le reste."}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/particuliers">
                  <Button size="lg" className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-7 text-base font-bold rounded-xl btn-shine" data-testid="hero-cta-reserver">
                    <Calendar className="w-5 h-5 mr-2" />
                    {"R\xe9server maintenant"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:0625168027">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white px-8 py-7 text-base hover:bg-white/10 rounded-xl" data-testid="hero-cta-appeler">
                    <Phone className="w-5 h-5 mr-2" />
                    06 25 16 80 27
                  </Button>
                </a>
              </div>
            </div>

            <div className="animate-fade-in-right hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-500/20 rounded-3xl blur-xl"></div>
                <div className="relative bg-white rounded-3xl p-10 shadow-2xl">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-emerald-50 px-4 py-2 rounded-full mb-4">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-bold text-emerald-700">{"Avance imm\xe9diate"}</span>
                    </div>
                    <p className="text-gray-500 text-sm mb-2">{`M\xe9nage \xe0 domicile`}</p>
                    <div className="flex items-baseline justify-center gap-3">
                      <span className="text-2xl text-gray-300 line-through font-medium">{`36\u20AC`}</span>
                      <span className="text-6xl font-black text-[#003264]">{`18\u20AC`}</span>
                      <span className="text-gray-500 text-lg">/h</span>
                    </div>
                    <p className="text-emerald-600 font-medium text-sm mt-2">{`Co\xfbt r\xe9el apr\xe8s cr\xe9dit d'imp\xf4t`}</p>
                  </div>

                  <div className="space-y-3 mb-8">
                    {[
                      `\xc9quipe qualifi\xe9e et v\xe9rifi\xe9e`,
                      "Intervention dans tout le 34",
                      "Satisfaction garantie",
                      `R\xe9servation en ligne 24h/24`
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/particuliers">
                    <Button className="w-full bg-[#003264] hover:bg-[#002244] text-white py-6 text-base rounded-xl btn-shine" data-testid="price-card-cta">
                      Obtenir mon devis gratuit
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== TRUST STRIP ====== */}
      <section className="bg-white border-b border-gray-100" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
            {[
              { icon: Star, text: "Note 4.9/5", sub: "Clients satisfaits" },
              { icon: Shield, text: `\xc9quipe v\xe9rifi\xe9e`, sub: `Personnel qualifi\xe9` },
              { icon: MapPin, text: `Tout l'H\xe9rault`, sub: `D\xe9partement 34` },
              { icon: Banknote, text: `Cr\xe9dit d'imp\xf4t`, sub: "-50% automatique" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-5 px-4 md:px-6 justify-center">
                <div className="w-10 h-10 rounded-xl bg-[#003264]/5 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#003264]" />
                </div>
                <div>
                  <p className="font-bold text-[#003264] text-sm">{item.text}</p>
                  <p className="text-xs text-gray-500">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SERVICES ====== */}
      <section className="py-20 lg:py-24 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#003264] mb-3">
              Nos prestations
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto">
              {`Des services adapt\xe9s \xe0 vos besoins, avec l'avance imm\xe9diate du cr\xe9dit d'imp\xf4t`}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Home,
                title: `M\xe9nage r\xe9gulier`,
                desc: "Entretien complet de votre domicile. Sols, surfaces, cuisine et salle de bain.",
                price: `18\u20AC/h`,
                priceFull: `36\u20AC/h`,
                alt: `ou 1,50\u20AC/m\xb2`,
                features: ["Aspiration & lavage sols", "Nettoyage surfaces", "Cuisine & salle de bain"],
                popular: false
              },
              {
                icon: LayoutGrid,
                title: "Nettoyage de vitres",
                desc: `Vitres int\xe9rieures et ext\xe9rieures, encadrements inclus.`,
                price: `3\u20AC/vitre`,
                priceFull: `6\u20AC/vitre`,
                alt: null,
                features: [`Vitres int\xe9rieures`, "Encadrements", `Baies vitr\xe9es`],
                popular: true
              },
              {
                icon: Sparkles,
                title: `Remise en \xe9tat`,
                desc: `Apr\xe8s travaux ou d\xe9m\xe9nagement. Nettoyage en profondeur.`,
                price: "-50%",
                priceFull: "Sur devis",
                alt: null,
                features: ["Fin de chantier", `\xc9tat des lieux`, "Nettoyage profond"],
                popular: false
              }
            ].map((service, i) => (
              <div key={i} className={`relative bg-white rounded-2xl overflow-hidden card-hover border-2 ${service.popular ? 'border-[#003264]' : 'border-gray-100'}`}>
                {service.popular && (
                  <div className="absolute top-4 right-4 bg-[#003264] text-white text-xs font-bold px-3 py-1 rounded-full">
                    Populaire
                  </div>
                )}
                <div className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-[#003264]/10 flex items-center justify-center mb-5">
                    <service.icon className="w-7 h-7 text-[#003264]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#003264] mb-2">{service.title}</h3>
                  <p className="text-gray-500 text-sm mb-6">{service.desc}</p>
                  
                  <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-gray-400 line-through">{service.priceFull}</span>
                      <span className="text-3xl font-black text-emerald-600">{service.price}</span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      {service.alt || `apr\xe8s cr\xe9dit d'imp\xf4t`}
                    </p>
                  </div>

                  <ul className="space-y-2.5 mb-8">
                    {service.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2.5 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link to="/particuliers">
                    <Button className={`w-full py-5 rounded-xl ${service.popular 
                      ? 'bg-[#003264] hover:bg-[#002244] text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-[#003264]'}`} 
                      data-testid={`service-cta-${i}`}>
                      {`R\xe9server`}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== HOW IT WORKS ====== */}
      <section className="py-20 lg:py-24 bg-white" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#003264] mb-3">
              {`Comment \xe7a marche ?`}
            </h2>
            <p className="text-gray-500">{`R\xe9servez en 2 minutes, on s'occupe du reste`}</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { num: "1", title: "Configurez", desc: `Choisissez votre prestation et s\xe9lectionnez un cr\xe9neau` },
              { num: "2", title: `R\xe9servez`, desc: `Cr\xe9ez votre compte en quelques secondes` },
              { num: "3", title: "On intervient", desc: `Notre \xe9quipe arrive \xe0 l'heure pr\xe9vue` },
              { num: "4", title: "-50% automatique", desc: `Le cr\xe9dit d'imp\xf4t est appliqu\xe9 directement` },
            ].map((step, i) => (
              <div key={i} className="text-center relative">
                <div className="w-14 h-14 rounded-2xl bg-[#003264] text-white flex items-center justify-center mx-auto mb-4 text-xl font-black">
                  {step.num}
                </div>
                <h3 className="font-bold text-[#003264] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500">{step.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-7 left-[calc(50%+35px)] w-[calc(100%-70px)] h-px bg-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SIMULATEUR ====== */}
      <section className="py-20 lg:py-24 bg-gray-50" data-testid="simulateur-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#003264] mb-3">
              Estimez votre devis
            </h2>
            <p className="text-gray-500">{`Obtenez une estimation instantan\xe9e du co\xfbt avec le cr\xe9dit d'imp\xf4t`}</p>
          </div>

          <div className="bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden" data-testid="simulateur">
            <div className="flex border-b border-gray-200">
              <button onClick={() => setActiveTab("menage")}
                className={`flex-1 py-4 px-6 text-sm font-semibold transition-all relative ${activeTab === "menage" ? "text-[#003264] bg-[#003264]/5" : "text-gray-500 hover:text-gray-700"}`}>
                <Home className="w-4 h-4 inline mr-2" />{`M\xe9nage`}
                {activeTab === "menage" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#003264]"></div>}
              </button>
              <button onClick={() => setActiveTab("vitres")}
                className={`flex-1 py-4 px-6 text-sm font-semibold transition-all relative ${activeTab === "vitres" ? "text-[#003264] bg-[#003264]/5" : "text-gray-500 hover:text-gray-700"}`}>
                <LayoutGrid className="w-4 h-4 inline mr-2" />Vitres
                {activeTab === "vitres" && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#003264]"></div>}
              </button>
            </div>

            <div className="p-6 md:p-8">
              {activeTab === "menage" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setMode("m2")}
                      className={`py-4 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${mode === "m2" ? "bg-[#003264] text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                      <Square className="w-5 h-5" /> {`Au m\xb2`}
                    </button>
                    <button onClick={() => setMode("heures")}
                      className={`py-4 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${mode === "heures" ? "bg-[#003264] text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                      <Clock className="w-5 h-5" /> {`\xc0 l'heure`}
                    </button>
                  </div>
                  <Input type="number" min="1"
                    placeholder={mode === "m2" ? `Surface en m\xb2` : "Nombre d'heures"}
                    value={valeur} onChange={(e) => setValeur(e.target.value)}
                    className="h-14 text-lg border-2 focus:border-[#003264] rounded-xl" />
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                    {mode === "m2" 
                      ? <span>{`3\u20AC/m\xb2 TTC \u2192 `}<strong className="text-emerald-600">{`1,50\u20AC/m\xb2 apr\xe8s cr\xe9dit d'imp\xf4t`}</strong></span>
                      : <span>{`36\u20AC/h TTC \u2192 `}<strong className="text-emerald-600">{`18\u20AC/h apr\xe8s cr\xe9dit d'imp\xf4t`}</strong></span>
                    }
                  </p>
                </div>
              )}

              {activeTab === "vitres" && (
                <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Vitres</p>
                    {vitresItems.map(item => (
                      <CounterRow key={item.key} item={item} value={vitres[item.key]} onUpdate={updateVitre} />
                    ))}
                  </div>
                  <div className="pt-4">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Encadrements</p>
                    {encadrementsItems.map(item => (
                      <CounterRow key={item.key} item={item} value={encadrements[item.key]} onUpdate={updateEncadrement} />
                    ))}
                  </div>
                </div>
              )}

              {currentDevis && (
                <div className="mt-6 pt-6 border-t-2 border-gray-100">
                  <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5 mb-4" data-testid="simulateur-credit-impot">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 line-through">{currentDevis.prix_total_ttc.toFixed(0)}{`\u20AC TTC`}</p>
                        <p className="text-4xl font-black text-emerald-600">{currentDevis.prix_apres_credit_impot.toFixed(0)}{`\u20AC`}</p>
                        <p className="text-xs text-emerald-600 font-medium mt-1">{`co\xfbt r\xe9el pour vous`}</p>
                      </div>
                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-xl font-black text-emerald-700">-50%</span>
                      </div>
                    </div>
                  </div>
                  <Link to="/particuliers">
                    <Button className="w-full bg-[#003264] hover:bg-[#002244] text-white py-6 text-base rounded-xl btn-shine" data-testid="simulateur-reserver-btn">
                      {`R\xe9server maintenant`}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ====== PRO CTA ====== */}
      <section className="py-20 bg-[#003264]" data-testid="pro-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm font-medium mb-6">
            Offre Professionnels
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {`Vous \xeates un professionnel ?`}
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            {`Bureaux, commerces, copropri\xe9t\xe9s... Obtenez un devis personnalis\xe9 pour l'entretien de vos locaux`}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/professionnels">
              <Button size="lg" className="w-full sm:w-auto bg-white text-[#003264] hover:bg-gray-100 px-8 py-6 text-base font-bold rounded-xl">
                Demander un devis pro
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:0625168027">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base rounded-xl">
                <Phone className="w-5 h-5 mr-2" />
                Nous appeler
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="py-20 bg-white" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#003264] mb-4">
            {`Pr\xeat \xe0 commencer ?`}
          </h2>
          <p className="text-gray-500 mb-3">
            {`R\xe9servez votre premi\xe8re prestation et ne payez que la moiti\xe9`}
          </p>
          <p className="text-xl font-bold text-emerald-600 mb-8">
            <span className="text-gray-400 line-through text-base mr-2">{`36\u20AC/h`}</span>
            {`D\xe8s 18\u20AC/h \u2014 co\xfbt r\xe9el`}
          </p>
          <Link to="/particuliers">
            <Button size="lg" className="bg-[#003264] hover:bg-[#002244] text-white px-10 py-7 text-base font-bold rounded-xl btn-shine" data-testid="final-cta-reserver">
              <Calendar className="w-5 h-5 mr-2" />
              {`R\xe9server maintenant`}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
